import type { AuthConfig, AuthUtilities } from "@urql/exchange-auth";

import type { LogoutEvent } from "../auth/types";

type GetTokenResult = { token: string; expires: number } | undefined;

export interface AuthExchangeHandlerHelpers {
  getToken: () => GetTokenResult;
  getRefreshToken: () => string | undefined;
  logout: (event?: LogoutEvent) => void;
  refreshToken: () => Promise<boolean>;
}

/**
 *  urql exchange to handle auth including triggering token refresh
 *  ** we can pass callbacks to allow Client to update auth **
 *  https://www.npmjs.com/package/@urql/exchange-auth
 */
export const authExchangeHandler =
  ({ getToken, logout, refreshToken }: AuthExchangeHandlerHelpers) =>
  // eslint-disable-next-line @typescript-eslint/require-await
  async (utils: AuthUtilities): Promise<AuthConfig> => {
    // called on initial launch,
    // fetch the auth state from storage (local storage, async storage etc)
    // let token = localStorage.getItem('token');
    // let refreshToken = localStorage.getItem('refreshToken');

    // TODO we could return a class with 'expires' to proactively refresh
    return {
      addAuthToOperation(operation) {
        const { token } = getToken() ?? {};
        if (token) {
          return utils.appendHeaders(operation, {
            Authorization: `Bearer ${token}`,
          });
        }
        return operation;
      },

      willAuthError(_operation) {
        const { expires, token } = getToken() ?? {};
        if (expires && token) {
          return expires <= Date.now();
        }

        return true;
      },

      didAuthError(error) {
        // check if the error was an auth error
        // this can be implemented in various ways, e.g. 401 or a special error code
        // return error.graphQLErrors.some(
        //   (e) => e.extensions?.code === 'FORBIDDEN',
        // );

        const msg = error.message.toLowerCase();
        const isAuthError =
          msg.includes("jwt expired") || msg.includes("auth error:");
        return isAuthError;
      },

      async refreshAuth() {
        const refreshed = await refreshToken();

        if (!refreshed) {
          logout();
        }
      },
    };
  };
